import "./Subcontractor.scss";
import InfoContext from "pages/InfoContext";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import { Controller } from "utils/Controller";
import { FormattedMessage } from "react-intl";
import { TARIFICATION_TYPE, VAR_TYPES } from "constants/constants";
import { Typography } from "core/typography/Typography";
import {
  renderInputManager,
  renderInputBranch,
  renderInputStartDateContract,
} from "../CommonInputs";
import { useContext, useEffect } from "react";
import {
  SUBCONTRACTOR_FORM_LABELS,
  SUBCONTRACTOR_FORM_PLACEHOLDERS,
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
  REMUNERATION_OPTIONS,
  STAFF_FORM_PLACEHOLDERS,
  AFFECT_MANAGER_LABEL,
  PAYMENT_DEADLINE_OPTIONS,
  RULES,
} from "constants/forms";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import PropTypes from "prop-types";
import { getCellErrorHelper } from "utils/helper";

const SubcontractorCorporateInfoForm = ({
  control,
  register,
  unregister,
  setValue,
  watch,
  errors,
  readOnly = false,
}) => {
  const {
    branches,
    currencyList,
    getOptions,
    id,
    cellHighlight,
    subcontractorBySlug,
    apiErrorResponse,
  } = useContext(InfoContext);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();

  const manager_id = watch("manager_id", []);
  const branch_id = watch("branch_id", []);
  const remuneration_type = watch("remuneration_type");
  const comment = watch("comment");
  const currency_id = watch("currency_id", "");

  const isDisabledCell =
    !readOnly && subcontractorBySlug ? "cell-disabled" : "";

  useEffect(() => {
    if (remuneration_type === TARIFICATION_TYPE.DAILY) {
      unregister("remuneration_other");
    } else if (remuneration_type === TARIFICATION_TYPE.OTHER) {
      unregister("remuneration_tarification");
    }
  }, [remuneration_type]);

  const renderInputCurrency = () => (
    <Controller
      {...{
        control,
        register,
        name: "currency_id",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSelect
            id="currency_id"
            maxHeight={160}
            label={CONSULTANT_FORM_LABELS.currency_id}
            placeholder={CONSULTANT_FORM_PLACEHOLDERS.currency_id}
            options={currencyList.map((elm) => ({
              label: elm.currency_symbol,
              value: elm.id,
              id: elm.id,
            }))}
            required
            errorMessage={apiErrorResponse?.["currency_id"]}
            setValue={setValue}
            value={currency_id}
            readOnly={readOnly}
            errors={errors["currency_id"]}
            {...props}
          />
        ),
      }}
    />
  );

  const renderInputPricingOther = () => (
    <Controller
      {...{
        control,
        register,
        name: "remuneration_other",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputText
            id="remuneration_other"
            label={SUBCONTRACTOR_FORM_LABELS.remuneration_other}
            required
            setValue={setValue}
            readOnly={readOnly}
            errors={errors["remuneration_other"]}
            errorMessage={apiErrorResponse?.["remuneration_other"]}
            {...props}
            true
          />
        ),
      }}
    />
  );

  const renderInputPricing = () => (
    <Controller
      {...{
        control,
        register,
        name: "remuneration_tarification",
        rules: {
          required: true,
          pattern: {
            value: RULES.positive_max_99999_decimal,
            message: "api_error_remuneration_tarification",
          },
        },
        render: (props) => (
          <InputText
            id="remuneration_tarification"
            type={VAR_TYPES.NUMBER}
            errors={errors["remuneration_tarification"]}
            errorMessage={
              apiErrorResponse?.["remuneration_tarification"] ||
              errors?.["remuneration_tarification"]?.message ||
              null
            }
            label={SUBCONTRACTOR_FORM_LABELS.remuneration_tarification}
            placeholder={
              SUBCONTRACTOR_FORM_PLACEHOLDERS.remuneration_tarification
            }
            readOnly={readOnly}
            required
            setValue={setValue}
            {...props}
          />
        ),
      }}
    />
  );
  const renderInputRemuneration = () => (
    <Controller
      {...{
        control,
        register,
        name: "remuneration_type",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSelect
            id="remuneration_type"
            label={SUBCONTRACTOR_FORM_LABELS.remuneration}
            placeholder={SUBCONTRACTOR_FORM_PLACEHOLDERS.remuneration}
            readOnly={readOnly}
            errors={errors["remuneration_type"]}
            errorMessage={apiErrorResponse?.["remuneration_type"]}
            required
            setValue={setValue}
            options={REMUNERATION_OPTIONS}
            {...props}
          />
        ),
      }}
    />
  );

  const renderInputComment = () => (
    <Controller
      {...{
        control,
        register,
        comment,
        name: "remuneration_comment",
        render: (props) => (
          <InputText
            errors={errors["remuneration_comment"]}
            label={SUBCONTRACTOR_FORM_LABELS.comment}
            readOnly={readOnly}
            value={comment}
            setValue={setValue}
            id="remuneration_comment"
            marginLeft={15}
            maxLength={60}
            comment={comment}
            setComment={(val) => setValue("comment", val)}
            {...props}
          />
        ),
      }}
    />
  );

  const renderInputPaymentDeadline = () => (
    <Controller
      {...{
        control,
        register,
        name: "payment_deadline",
        rules: {
          required: true,
        },
        render: (props) => (
          <InputSelect
            id="payment_deadline"
            errors={errors["payment_deadline"]}
            errorMessage={apiErrorResponse?.["payment_deadline"]}
            label={SUBCONTRACTOR_FORM_LABELS.payment_deadline}
            placeholder={SUBCONTRACTOR_FORM_PLACEHOLDERS.payment_deadline}
            readOnly={readOnly}
            setValue={setValue}
            required
            options={PAYMENT_DEADLINE_OPTIONS}
            {...props}
          />
        ),
      }}
    />
  );

  const remunerationFields = {
    [TARIFICATION_TYPE.DAILY]: "remuneration_tarification",
    [TARIFICATION_TYPE.OTHER]: "remuneration_other",
  };

  const field = remunerationFields[remuneration_type];

  const hasRemunerationValue = field ? watch(field) : false;

  const apiError = field ? apiErrorResponse?.[field] || errors?.[field] : false;

  return (
    <div style={{ width: "100%" }}>
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="corporate_info"
            defaultMessage="Infos Corporate"
          />
        }
      />
      <div
        className="subcontractorcorporate_form"
        id="subcontractorcorporate_form"
      >
        <div className="line-1">
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: remuneration_type,
              hasError:
                errors?.["remuneration_type"] ||
                apiErrorResponse?.["remuneration_type"],
              cellHighlight,
            })} `}
          >
            {renderInputRemuneration()}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: watch("payment_deadline"),
              hasError:
                errors?.["payment_deadline"] ||
                apiErrorResponse?.["payment_deadline"],
              cellHighlight,
            })} `}
          >
            {renderInputPaymentDeadline()}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: hasRemunerationValue,
              hasError: apiError,
              cellHighlight,
            })} `}
          >
            {remuneration_type === TARIFICATION_TYPE.DAILY
              ? renderInputPricing()
              : renderInputPricingOther()}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: watch("currency_id"),
              hasError:
                errors?.["currency_id"] || apiErrorResponse?.["currency_id"],
              cellHighlight,
            })} `}
          >
            {renderInputCurrency()}
          </div>
          <div
            className={`cell-padding ${getCellErrorHelper({
              hasValue: watch("branch_id"),
              hasError:
                errors?.["branch_id"] || apiErrorResponse?.["branch_id"],
              cellHighlight,
            })} `}
          >
            {renderInputBranch({
              label: SUBCONTRACTOR_FORM_LABELS.branch,
              placeholder: SUBCONTRACTOR_FORM_PLACEHOLDERS.branch,
              branches,
              control,
              register,
              setValue,
              errors: errors?.["branch_id"],
              errorMessage: apiErrorResponse?.["branch_id"],
              readOnly,
              value: branch_id,
            })}
          </div>
          <div
            className={`cell-padding ${isDisabledCell} ${getCellErrorHelper({
              hasValue: watch("manager_id"),
              hasError:
                errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
              cellHighlight,
            })} `}
          >
            {renderInputManager({
              control,
              errors:
                errors?.["manager_id"] || apiErrorResponse?.["manager_id"],
              getOptions,
              label: AFFECT_MANAGER_LABEL,
              placeholder: STAFF_FORM_PLACEHOLDERS.manager_id,
              readOnly,
              register,
              setValue,
              value: manager_id,
              getNamesByFilters: getNamesByFilters,
              namesByFilters: namesByFilters,
              errorMessage:
                errors?.["manager_id"]?.message ||
                apiErrorResponse?.["manager_id"],
            })}
          </div>
          {id && (
            <div
              className={`cell-padding ${getCellErrorHelper({
                hasValue: watch("contract_start_date"),
                hasError:
                  errors?.["contract_start_date"] ||
                  apiErrorResponse?.["contract_start_date"],
                cellHighlight,
              })} `}
            >
              {renderInputStartDateContract({
                control,
                errors,
                label: SUBCONTRACTOR_FORM_LABELS.contract_start_date,
                placeholder:
                  SUBCONTRACTOR_FORM_PLACEHOLDERS.contract_start_date,
                register,
                readOnly: true,
                setValue,
              })}
            </div>
          )}
        </div>
        <div className="line-2">
          <div className="cell-padding">{renderInputComment()}</div>
        </div>
      </div>
    </div>
  );
};

SubcontractorCorporateInfoForm.propTypes = {
  control: PropTypes.object,
  register: PropTypes.func,
  unregister: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  setErrors: PropTypes.func,
};

export default SubcontractorCorporateInfoForm;
